"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const vue_1 = require("vue");
const Payment_1 = require("@/common/inputs/Payment");
const Spendable_1 = require("@/common/inputs/Spendable");
const PaymentWidgetMixin_1 = require("@/client/mixins/PaymentWidgetMixin");
const PreferencesManager_1 = require("@/client/utils/PreferencesManager");
const AppButton_vue_1 = __importDefault(require("@/client/components/common/AppButton.vue"));
const PaymentUnit_vue_1 = __importDefault(require("@/client/components/PaymentUnit.vue"));
exports.default = (0, vue_1.defineComponent)({
    name: 'SelectPayment',
    props: {
        playerView: {
            type: Object,
            required: true,
        },
        playerinput: {
            type: Object,
            required: true,
        },
        onsave: {
            type: Function,
            required: true,
        },
        showsave: {
            type: Boolean,
        },
        showtitle: {
            type: Boolean,
        },
    },
    computed: {
        ...PaymentWidgetMixin_1.PaymentWidgetMixin.computed,
        thisPlayer() {
            return this.playerView.thisPlayer;
        },
        SPENDABLE_RESOURCES() {
            return [
                'steel',
                'titanium',
                'heat',
                'seeds',
                'auroraiData',
                'kuiperAsteroids',
                'spireScience',
                'megacredits',
            ];
        },
    },
    components: {
        AppButton: AppButton_vue_1.default,
        PaymentUnitComponent: PaymentUnit_vue_1.default,
    },
    data() {
        return {
            cost: 0,
            payment: { ...Payment_1.Payment.EMPTY },
            warning: undefined,
        };
    },
    mounted() {
        (0, vue_1.nextTick)(() => {
            this.setInitialCost();
            this.payment.megacredits = this.getMegaCreditsMax();
            this.setDefaultValues();
        });
    },
    methods: {
        ...PaymentWidgetMixin_1.PaymentWidgetMixin.methods,
        hasWarning() {
            return this.warning !== undefined;
        },
        showReserveWarning(unit) {
            const reserveUnits = this.playerinput.reserveUnits;
            if (reserveUnits === undefined)
                return false;
            switch (unit) {
                case 'megacredits':
                case 'steel':
                case 'titanium':
                case 'plants':
                case 'heat':
                    return reserveUnits[unit] > 0 && this.canUse(unit);
            }
            return false;
        },
        setInitialCost() {
            this.cost = this.playerinput.amount ?? 0;
        },
        setDefaultValue(mcAlreadyCovered, unit) {
            if (!this.canUse(unit)) {
                return 0;
            }
            const reserve = this.playerinput.reserveUnits?.[unit] ?? 0;
            const availableUnits = Math.max(this.getAvailableUnits(unit) - reserve, 0);
            if (availableUnits === 0) {
                return 0;
            }
            const cost = this.cost;
            const targetResourceRate = this.getResourceRate(unit);
            let contributingUnits = Math.ceil(Math.max(cost - this.getAvailableUnits('megacredits') - mcAlreadyCovered, 0) / targetResourceRate);
            contributingUnits = Math.min(contributingUnits, availableUnits);
            let contributingMCValue = contributingUnits * targetResourceRate;
            const greedy = unit !== 'heat';
            if (greedy === true) {
                while (contributingUnits < availableUnits && contributingMCValue <= cost - targetResourceRate) {
                    contributingUnits++;
                    contributingMCValue += targetResourceRate;
                }
            }
            this.payment[unit] = contributingUnits;
            return contributingMCValue;
        },
        setDefaultValues(reserveMegacredits = false) {
            const cost = this.cost;
            const megacredits = this.getAvailableUnits('megacredits');
            let amountCovered = reserveMegacredits ? megacredits : 0;
            for (const unit of ['seeds', 'auroraiData', 'steel', 'titanium', 'heat', 'spireScience', 'kuiperAsteroids']) {
                amountCovered += this.setDefaultValue(amountCovered, unit);
            }
            if (!reserveMegacredits) {
                this.payment.megacredits = Math.min(megacredits, Math.max(cost - amountCovered, 0));
            }
        },
        setMaxMCValue() {
            this.setMaxValue('megacredits');
            this.setDefaultValues(true);
        },
        canAffordWithMcOnly() {
            return this.thisPlayer.megacredits >= this.cost;
        },
        canUse(unit) {
            if (unit === 'megacredits') {
                return true;
            }
            if (unit === 'titanium') {
                if (this.thisPlayer.titanium === 0) {
                    return false;
                }
                return this.playerinput.paymentOptions.titanium === true || this.playerinput.paymentOptions.lunaTradeFederationTitanium === true;
            }
            return this.playerinput.paymentOptions[unit] === true && this.hasUnits(unit);
        },
        saveData() {
            let totalSpent = 0;
            for (const target of Spendable_1.SPENDABLE_RESOURCES) {
                totalSpent += this.payment[target] * this.getResourceRate(target);
            }
            for (const target of Spendable_1.SPENDABLE_RESOURCES) {
                if (this.payment[target] > this.getAvailableUnits(target)) {
                    this.warning = `You do not have enough ${target}`;
                    return;
                }
            }
            const requiredAmt = this.playerinput.amount || 0;
            if (requiredAmt > 0 && totalSpent < requiredAmt) {
                this.warning = 'Haven\'t spent enough';
                return;
            }
            if (requiredAmt === 0) {
                this.payment.heat = 0;
                this.payment.megacredits = 0;
            }
            if (requiredAmt > 0 && totalSpent > requiredAmt) {
                const diff = totalSpent - requiredAmt;
                for (const target of Spendable_1.SPENDABLE_RESOURCES) {
                    if (this.payment[target] && diff >= this.getResourceRate(target)) {
                        this.warning = `You cannot overspend ${target}`;
                        return;
                    }
                }
            }
            const showAlert = (0, PreferencesManager_1.getPreferences)().show_alerts;
            if (requiredAmt > 0 && totalSpent > requiredAmt && showAlert) {
                const diff = totalSpent - requiredAmt;
                if (!confirm('Warning: You are overpaying by ' + diff + ' M€')) {
                    this.warning = 'Please adjust payment amount';
                    return;
                }
            }
            this.onsave({ type: 'payment', payment: this.payment });
        },
        onMaxClicked(unit) {
            if (unit === 'megacredits') {
                this.setMaxMCValue();
            }
            else {
                this.setMaxValue(unit);
            }
        },
    },
});
